import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import { parse, format } from 'date-fns';

import * as XLSX from 'xlsx';

import { currentUserSessionAtom } from 'library/store';

const _ = () => {

    const { t, i18n } = useTranslation();

    const { language: lg, applicationSetup } = useRecoilValue(currentUserSessionAtom);

    const sanitizeFileName = (name: string): string =>
        name.replace(/[/\\?%*:|<>]/g, '_')
            .replace(/\s+/g, ' ')
            .trim();

    const capitalizeFirstLetter = (str: string) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

    const compareName = (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase());

    function compareLocalizedName<T extends { id?: number }>(
        lg: string | undefined,
        getFR: (x: T) => string | undefined,
        getEN: (x: T) => string | undefined
    ) {
        return (a: T, b: T) => {
            const aLabel = (lg || '').includes('fr')
                ? (getFR(a) ?? getEN(a) ?? String(a.id ?? ''))
                : (getEN(a) ?? getFR(a) ?? String(a.id ?? ''));
            const bLabel = (lg || '').includes('fr')
                ? (getFR(b) ?? getEN(b) ?? String(b.id ?? ''))
                : (getEN(b) ?? getFR(b) ?? String(b.id ?? ''));
            return aLabel.localeCompare(bLabel);
        };
    }

    const toBoolean = (value: any): boolean => value === true;

    const getDateFormat = (): string => lg.includes('en') ? 'MM/dd/yyyy' : 'dd/MM/yyyy';

    const parseDate = (dateString: string) => {
        const formats = ['yyyy-MM-dd', getDateFormat()];

        for (const format of formats) {
            const parsedDate = parse(dateString, format, new Date());
            if (!isNaN(parsedDate.getTime()))
                return parsedDate;
        }
        return null;
    }

    const parseTime = (timeString: string) => {
        const formats = ['HH:mm', 'HH:mm:ss', 'HH:mm a', 'HH:mm:ss a'];

        for (const format of formats) {
            const parsedTime = parse(timeString, format, new Date());
            if (!isNaN(parsedTime.getTime()))
                return parsedTime;
        }
        return null;
    }

    const toDateWithoutSeconds = (value: string | Date): Date => {
        const date = new Date(value);
        return new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            0,
            0
        );
    };

    const hasOverlappingTimeSlots = (timeSlots: { startTime: Date, endTime: Date }[]): { hasOverlapping: boolean, error: string } => {
        const sortedSlots = [...timeSlots]
            .map(slot => ({
                startTime: toDateWithoutSeconds(slot.startTime),
                endTime: toDateWithoutSeconds(slot.endTime)
            }))
            .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

        for (let i = 1; i < sortedSlots.length; i++) {
            const prev = sortedSlots[i - 1];
            const curr = sortedSlots[i];

            if (curr.startTime.getTime() < prev.endTime.getTime()) {
                // Overlap detected
                console.log({ prev, curr });
                return { hasOverlapping: true, error: `${format(sortedSlots[i - 1].startTime, 'HH:mm')} - ${format(sortedSlots[i - 1].endTime, 'HH:mm')} <==> ${format(sortedSlots[i].startTime, 'HH:mm')} - ${format(sortedSlots[i].endTime, 'HH:mm')}` };
            }
        }

        return { hasOverlapping: false, error: '' }; // No overlaps
    }

    const generateRandomColor = (): string => {
        const r = Math.floor(Math.random() * 256); // Random value for red (0 to 255)
        const g = Math.floor(Math.random() * 256); // Random value for green (0 to 255)
        const b = Math.floor(Math.random() * 256); // Random value for blue (0 to 255)
        return `rgb(${r},${g},${b})`; // Return the color as an RGB string
    };

    const generatePastelColorFromName = (name: string): string => {
        let hash = 0;

        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        const hue = hash % 360;
        return `hsl(${hue}, 70%, 80%)`; // Soft pastel color
    };

    const generateDistinctColorFromName = (name: string): string => {
        let hash = 2166136261; // FNV-1a hash seed

        for (let i = 0; i < name.length; i++) {
            hash ^= name.charCodeAt(i);
            hash *= 16777619;
        }

        // Normalize to [0, 360)
        const hue = Math.abs(hash) % 360;

        // Vary saturation and lightness slightly for better distinction
        const saturation = 65 + (Math.abs(hash) % 10); // [65–74]%
        const lightness = 65 + (Math.abs(hash >> 8) % 10); // [65–74]%

        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };


    //const formatAmount = (num: )  new Intl.NumberFormat('fr-FR').format(total)

    const filterDistinctObjects = <T>(array: T[], prop1: keyof T, prop2: keyof T): T[] => {
        const seen = new Set<string>();
        return array.filter(item => {
            const key = `${item[prop1]}|${item[prop2]}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    };

    function hasDuplicate<T>(arr: T[], prop: keyof T): boolean {
        const seenValues = new Set();

        return arr.some(item => {
            const value = item[prop];
            if (seenValues.has(value)) {
                return true; // Duplicate found
            }
            seenValues.add(value);
            return false;
        });
    }

    function findDuplicates<T>(arr: T[], prop: keyof T): T[] {
        const seenValues = new Set();
        const duplicates: T[] = [];

        arr.forEach(item => {
            const value = item[prop];
            if (seenValues.has(value)) {
                duplicates.push(item); // Add to duplicates array if already seen
            } else {
                seenValues.add(value);
            }
        });

        return duplicates;
    }

    function findDuplicate<T>(arr: T[], prop: keyof T): T | undefined {
        const seenValues = new Set();

        arr.forEach(item => {
            const value = item[prop];
            if (seenValues.has(value)) {
                return item;
            } else {
                seenValues.add(value);
            }
        });

        return undefined;
    }

    function findDuplicate2<T>(arr: T[], prop1: keyof T, prop2: keyof T): T | undefined {
        const seenValues = new Set();

        arr.forEach(item => {
            const value = `${item[prop1]}-|-${item[prop2]}`;
            if (seenValues.has(value)) {
                return item;
            } else {
                seenValues.add(value);
            }
        });

        return undefined;
    }

    function findDuplicate3<T>(arr: T[], prop1: keyof T, prop2: keyof T, prop3: keyof T): T | undefined {
        const seenValues = new Set();

        arr.forEach(item => {
            const value = `${item[prop1]}-|-${item[prop2]}-|-${item[prop3]}`;
            if (seenValues.has(value)) {
                return item;
            } else {
                seenValues.add(value);
            }
        });

        return undefined;
    }

    async function readExcel(file: File): Promise<any[]> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                resolve(jsonData);
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    }

    function mergeTemplate(template: string, row: Record<string, any>): string {
        return template.replace(/@\$\{(\w+)\}/g, (_, key) => {
            return row[key] !== undefined && row[key] !== null
                ? String(row[key])
                : `missing:${key}`;
        });
    }

    function extractVariables(template: string): string[] {
        const regex = /@\$\{(\w+)\}/g;
        const variables: string[] = [];
        let match;
        while ((match = regex.exec(template)) !== null) {
            variables.push(match[1]);
        }
        return variables;
    }


    return {
        sanitizeFileName,
        capitalizeFirstLetter,
        compareName,
        compareLocalizedName,
        toBoolean,

        toDateWithoutSeconds,
        hasOverlappingTimeSlots,
        getDateFormat,
        parseDate,
        parseTime,

        generateRandomColor,
        generatePastelColorFromName,
        generateDistinctColorFromName,

        filterDistinctObjects,
        hasDuplicate,
        findDuplicate,
        findDuplicate2,
        findDuplicate3,
        findDuplicates,

        readExcel,
        mergeTemplate,
        extractVariables
    }

}

export default _;